import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import MetricsChart, { MetricDataPoint, ValueType } from '../MetricsChart';

interface TrendDataPoint {
  metric_date: string;
  metric_value: number;
  is_threshold_violated: boolean;
  lower_bound: number | null;
  upper_bound: number | null;
  value_type?: string | null;
  unit_of_measure?: string | null;
  target_value?: number | null;
  threshold_direction?: string | null;
  metrics_type?: string | null;
}

interface MetricTrendDialogProps {
  open: boolean;
  onClose: () => void;
  metricName: string;
  metricIdentifierName: string | null;
  reviewEntityName: string;
  metricType: string;
  valueType?: ValueType;
  unitOfMeasure?: string | null;
  metricsType?: string | null;
  targetValue?: number | null;
  trendData: TrendDataPoint[];
  loading?: boolean;
}

const MetricTrendDialog: React.FC<MetricTrendDialogProps> = ({
  open,
  onClose,
  metricName,
  metricIdentifierName,
  reviewEntityName,
  metricType,
  valueType,
  unitOfMeasure,
  metricsType,
  targetValue,
  trendData,
  loading = false,
}) => {
  // Derive valueType / UoM / metricsType / target from trendData if not explicitly provided
  const effectiveValueType = valueType || 
    (trendData.length > 0 ? trendData[0].value_type : undefined);
  const effectiveUnitOfMeasure = unitOfMeasure ||
    (trendData.length > 0 ? trendData[0].unit_of_measure : undefined);
  const effectiveMetricsType = metricsType ||
    (trendData.length > 0
      ? (trendData[0].metrics_type || trendData[0].threshold_direction)
      : undefined);
  const effectiveTargetValue = targetValue ??
    (trendData.length > 0 ? trendData[0].target_value : null);

  // Convert TrendDataPoint to MetricDataPoint format
  const chartData: MetricDataPoint[] = trendData.map((d) => ({
    metric_date: d.metric_date,
    metric_value: d.metric_value,
    is_threshold_violated: d.is_threshold_violated,
    lower_bound: d.lower_bound,
    upper_bound: d.upper_bound,
    target_value: d.target_value ?? effectiveTargetValue,
    metrics_type: d.metrics_type || d.threshold_direction || effectiveMetricsType,
    threshold_direction: d.threshold_direction || d.metrics_type || effectiveMetricsType,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" component="div">
              📈 Metric Trend
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {metricName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : trendData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Typography color="text.secondary">No historical data available</Typography>
          </Box>
        ) : (
          <MetricsChart
            data={chartData}
            metricName={metricName}
            reviewEntityName={reviewEntityName}
            metricType={metricType}
            metricIdentifierName={metricIdentifierName}
            valueType={effectiveValueType}
            unitOfMeasure={effectiveUnitOfMeasure}
            metricsType={effectiveMetricsType}
            targetValue={effectiveTargetValue}
            showChartTypeToggle={true}
            defaultChartType="line"
            showThresholds={true}
            showSummary={true}
            showMetadata={true}
            height={350}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MetricTrendDialog;
